/**
 * Módulo del tutorial (grupo "El negocio"): Declaración SIGSA (KAN-232).
 *
 * FLUJO PRINCIPAL = desde la sesión de carga (el que usan todos los días):
 * elegí la sesión → Ver Detalles → botón "Declaración SIGSA" → la tabla con
 * lo que pide SENASA → Descargar TXT → subirlo al portal con clave fiscal →
 * "✓ Ya lo declaré" que marca las caravanas para no repetirlas nunca más.
 * La declaración GENERAL (menú Ganado, todo el stock) se menciona como
 * alternativa al cierre.
 *
 * Anclas: menu-sesiones, sesion-card (card de la sesión), sesion-btn-sigsa
 * (detalle de la sesión), sigsa-btn-download, sigsa-btn-marcar-declarado.
 * Los pasos de la tabla no fijan `route`: la página de declaración vive en
 * /sesiones/{código}/sigsa (dinámico) y también en /declaracion-sigsa.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { getRepositories } from '../../../services/storage';

export const sigsaModule: TutorialModuleDef = {
  id: 'sigsa',
  title: 'Declaración SIGSA sin dolores',
  description: 'Desde tu sesión de carga al TXT listo para SENASA: revisá los datos, descargá el archivo y marcá lo declarado para no repetirlo nunca más.',
  emoji: '📋',
  image: 'images/tutorial/sigsa.png',
  permission: { module: 'sigsa', action: 'create' },
  grupo: 'negocio',
  route: '/sesiones',
  steps: [
    {
      id: 'concepto-sigsa',
      route: '/tutorial',
      text: 'La declaración de stock ante SENASA, sin planillas: se arranca desde la sesión que cargaste, y la app arma el archivo por vos.',
      narration:
        'Y ahora, el trámite que más dolor de cabeza da: la declaración SIGSA. Acá la cosa es al revés de lo que estás acostumbrado: no llenás ninguna planilla. La declaración se arranca desde la misma sesión que cargaste, y la app ya sabe qué animales tiene: te arma el archivo lista la declarar. Vos sólo revisás y descargás. Quedate tranquilo que miramos todo junto.',
    },
    {
      id: 'ir-a-sesiones-sigsa',
      target: '[data-ar-guide="menu-sesiones"]',
      label: 'Sesiones (menú Ganado)',
      text: 'Andá al menú, sección Ganado, y tocá "Sesiones" (en celular: menú ☰): la declaración arranca desde la sesión que querés declarar.',
      narration:
        'Primero, a buscar la hacienda: andá al menú, sección Ganado, y tocá Sesiones. La declaración se hace sobre una sesión puntual — la que cargaste y querés declarar — así que arrancamos por ahí, que es el camino de todos los días.',
    },
    {
      id: 'elegir-sesion',
      route: '/sesiones',
      target: '[data-ar-guide="sesion-ver-detalles"]',
      label: 'Ver Detalles (de la sesión a declarar)',
      text: 'Elegí la sesión con los animales que querés declarar y tocá su botón "Ver Detalles" — seguimos solos.',
      narration:
        'Ahora buscá en la lista la sesión que querés declarar, y tocá su botón verde de Ver Detalles. Con ese toque seguimos solos: adentro están sus animales, sus pesos, y el botón que nos trae acá.',
    },
    {
      id: 'boton-declarar',
      target: '[data-ar-guide="sesion-btn-sigsa"]',
      label: 'Declaración SIGSA (en el detalle)',
      text: 'En el detalle de la sesión tocá "Declaración SIGSA": se arma la declaración con los animales de ESTA sesión, ya listos para SENASA.',
      narration:
        '¿Lo ves? Acá en el detalle de la sesión está el botón que importa: Declaración SIGSA. Tocálo, y se arma la declaración con los animales de esta sesión nada más, cada uno con lo que pide SENASA. Listo el trámite de preparar la planilla: ya está preparada.',
    },
    {
      id: 'la-tabla',
      text: 'Acá están tus animales con lo que pide SENASA: caravana, sexo, raza y fecha de nacimiento. El botón de descargar se habilita recién cuando TODOS tienen sus datos completos.',
      narration:
        'Mirá la tabla: cada animal con su caravana, su sexo, su raza y su fecha de nacimiento. Eso es lo que viaja en el archivo. Y grabate la regla de oro: el botón de descargar se prende recién cuando todos los animales tienen sus datos completos. Lo que falta queda pintado en rojo, y arriba la app te dice exactamente qué falta.',
    },
    {
      id: 'completar-datos',
      text: 'Lo que falte está pintado en ROJO en la tabla: corregilo en la misma fila, al toque — y si son muchos, la edición en lote lo cambia de una. Guardá con "Guardar Cambios".',
      narration:
        'Fijate que lo que falta está pintado en rojo, no lo podés pasar por alto. Lo corregís en la misma fila, al toque: sexo, raza, fecha de nacimiento. Y si son muchos iguales, la edición en lote lo cambia de una sola vez. Cuando termines, tocá Guardar Cambios y queda asentado.',
    },
    {
      id: 'descargar-txt',
      target: '[data-ar-guide="sigsa-btn-download"]',
      label: '⬇ Descargar TXT',
      advanceOnClick: false,
      text: '"Descargar TXT" baja el archivo para subir a SIGSA — se habilita con todo completo. Si hay caravanas inválidas o RENSPAs mezclados, te frena hasta corregir.',
      narration:
        'Ahora sí: el botón de Descargar TXT se habilita cuando todo está completo, y te baja el archivito listo para subir al sistema de SENASA. Si está gris, es que a algún animal le falta un dato. Y la app se fija antes de dejarlo ir: una caravana mal escrita o animales de dos RENSPA distintos en el mismo lote te frenan hasta corregir. Mejor prevenir que declarar dos veces.',
    },
    {
      id: 'portal-sigsa',
      text: 'Con el archivo en la mano: entrá al portal de SENASA con tu clave fiscal (AFIP/ARCA) — en la página tenés los links directos — y subilo ahí.',
      narration:
        'Con el archivo en la mano, lo que sigue es afuera: abrí sesión en el portal de SENASA con tu clave fiscal, la de AFIP. Ahí subís el TXT y confirmás la declaración. En la página dejamos los links directos al login y al portal, así no andás buscándolos.',
    },
    {
      id: 'marcar-declarado',
      target: '[data-ar-guide="sigsa-btn-marcar-declarado"]',
      label: '✓ Ya lo declaré',
      advanceOnClick: false,
      text: 'Volvé a la app y tocá "✓ Ya lo declaré en SIGSA": esas caravanas quedan marcadas y no se vuelven a incluir en declaraciones futuras.',
      narration:
        'Y el paso que no te podés saltar: cuando ya lo declaraste, volvé acá y tocá el botón que dice Ya lo declaré en SIGSA. Con eso esas caravanas quedan marcadas como declaradas, y la app nunca más te las va a volver a incluir. Chau con declarar dos veces el mismo animal, un clásico.',
    },
    {
      id: 'cierre-sigsa',
      text: 'Listo: sesión declarada y al día. ¿Y si querés declarar todo el stock de una? Menú Ganado → "Declaración SIGSA": misma tabla, pero con todos tus animales.',
      narration:
        'Y así de simple queda el trámite más pesado del año: sesión declarada y al día. Un último dato: si algún día querés declarar todo el stock de una sola vez, en el menú Ganado está la Declaración SIGSA general — misma tabla, mismos botones, pero con todos tus animales juntos. Papel cero, memoria de elefante.',
    },
  ],
  completionCheck: async () => {
    const declaraciones = (await getRepositories().sigsaDeclaracion.getAllForActiveEmpresa()) as unknown[];
    return declaraciones.length > 0;
  },
};
